import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import LogoutButton from "../components/LogoutButton";
import Sidebar from "../components/Sidebar";
import api from "../utils/api";
import {
  FaTachometerAlt,
  FaFileInvoiceDollar,
  FaClipboardList,
  FaTint,
  FaBuilding,
  FaArrowRight,
  FaCheckCircle,
  FaFileAlt,
  FaClock,
  FaPlusCircle,
  FaSearch,
  FaEye,
  FaSync
} from "react-icons/fa";
import "../style/admin.css";

function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalReadings: 0,
    totalWings: 0,
    latestReadingDate: "N/A",
    loading: true,
  });
  const [recentReadings, setRecentReadings] = useState([]);
  const [modalImage, setModalImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);

  const fetchDashboardData = async () => {
    setStats((prev) => ({ ...prev, loading: true }));
    try {
      const [readingsRes, wingsRes] = await Promise.allSettled([
        api.get("/meter-readings/all-page"),
        api.get("/wings"),
      ]);

      let readingsCount = 0;
      let wingsCount = 0;
      let latestDate = "N/A";
      let recent = [];

      if (readingsRes.status === "fulfilled" && Array.isArray(readingsRes.value.data)) {
        const rawData = readingsRes.value.data;
        readingsCount = rawData.length;

        // Sort by id / date descending for recent items
        const sorted = [...rawData].reverse();
        recent = sorted.slice(0, 5).map((item) => ({
          id: item.id,
          wing: item.wingName || "N/A",
          flatNo: item.flatNo || "N/A",
          prevReading: item.previousReading || 0,
          currReading: item.currentReading || 0,
          date: item.date ? new Date(item.date).toLocaleDateString("en-GB") : "N/A",
        }));

        if (sorted[0] && sorted[0].date) {
          latestDate = new Date(sorted[0].date).toLocaleDateString("en-GB");
        }
      }

      if (wingsRes.status === "fulfilled" && Array.isArray(wingsRes.value.data)) {
        wingsCount = wingsRes.value.data.length;
      }

      setStats({
        totalReadings: readingsCount,
        totalWings: wingsCount || 4,
        latestReadingDate: latestDate,
        loading: false,
      });
      setRecentReadings(recent);
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const openImageModal = (id) => {
    setModalImage(null);
    setLoadingImage(true);
    api.get(`/meter-readings/getImageById/${id}`)
      .then((res) => {
        if (res.data && res.data.image) {
          setModalImage(`data:image/jpeg;base64,${res.data.image}`);
        } else {
          alert("No image available for this reading.");
        }
      })
      .catch((err) => {
        console.error("Error fetching image:", err);
        alert("Failed to load meter image.");
      })
      .finally(() => setLoadingImage(false));
  };

  const currentDateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="admin-layout">
      {/* Sidebar Navigation */}
      <Sidebar />

      {/* Main Workspace */}
      <div className="admin-workspace">
        {/* Top Header */}
        <header className="workspace-header">
          <div className="header-left">
            <span className="header-title">Dashboard Overview</span>
            <span className="header-date">{currentDateStr}</span>
          </div>
          <div className="header-right">
            <button className="refresh-btn" onClick={fetchDashboardData} title="Refresh Data">
              <FaSync className={stats.loading ? "spin" : ""} />
            </button>
            <button className="primary-header-btn" onClick={() => navigate("/home")}>
              <FaPlusCircle /> Data Entry Form
            </button>
            <div className="mobile-logout">
              <LogoutButton />
            </div>
          </div>
        </header>

        {/* Workspace Content */}
        <main className="workspace-content">
          {/* Hero Welcome Banner */}
          <div className="dashboard-hero">
            <div className="hero-text">
              <div className="hero-badge">
                <FaCheckCircle /> Life Republic 7 Avenue Co-op Housing Society
              </div>
              <h1>Welcome back, Admin</h1>
              <p>
                Monitor meter reading logs, generate wing-wise billing reports, inspect uploaded meter images, and oversee water collection data.
              </p>
            </div>
            <div className="hero-actions">
              <button className="btn-hero-primary" onClick={() => navigate("/adminreport")}>
                View All Reports <FaArrowRight />
              </button>
              <button className="btn-hero-secondary" onClick={() => navigate("/home")}>
                Open Collector Form
              </button>
            </div>
          </div>

          {/* KPI Metrics Cards */}
          <div className="kpi-grid">
            <div className="kpi-card blue">
              <div className="kpi-icon-box">
                <FaFileAlt />
              </div>
              <div className="kpi-data">
                <span className="kpi-value">{stats.loading ? "..." : stats.totalReadings}</span>
                <span className="kpi-label">Total Meter Readings</span>
              </div>
            </div>

            <div className="kpi-card teal">
              <div className="kpi-icon-box">
                <FaBuilding />
              </div>
              <div className="kpi-data">
                <span className="kpi-value">{stats.loading ? "..." : stats.totalWings}</span>
                <span className="kpi-label">Active Wings</span>
              </div>
            </div>

            <div className="kpi-card purple">
              <div className="kpi-icon-box">
                <FaClock />
              </div>
              <div className="kpi-data">
                <span className="kpi-value">{stats.loading ? "..." : stats.latestReadingDate}</span>
                <span className="kpi-label">Latest Reading Date</span>
              </div>
            </div>

            <div className="kpi-card green">
              <div className="kpi-icon-box">
                <FaCheckCircle />
              </div>
              <div className="kpi-data">
                <span className="kpi-value">Operational</span>
                <span className="kpi-label">System Status</span>
              </div>
            </div>
          </div>

          {/* Quick Action Modules */}
          <div className="action-modules-header">
            <h2>Quick Actions & Portals</h2>
            <p>Direct access to primary water billing features</p>
          </div>

          <div className="action-cards-grid">
            <div className="action-card" onClick={() => navigate("/adminreport")}>
              <div className="action-card-header">
                <div className="action-icon-box icon-reports">
                  <FaFileInvoiceDollar />
                </div>
                <span className="action-tag tag-blue">Reports & Analytics</span>
              </div>
              <h3>Water Billing Reports</h3>
              <p>Filter logs by Wing, Flat No, or Date. Export reports to Excel/PDF and inspect uploaded meter image evidence.</p>
              <div className="action-link">
                <span>Access Reports</span> <FaArrowRight />
              </div>
            </div>

            <div className="action-card" onClick={() => navigate("/home")}>
              <div className="action-card-header">
                <div className="action-icon-box icon-entry">
                  <FaTint />
                </div>
                <span className="action-tag tag-teal">Public Data Entry</span>
              </div>
              <h3>Water Reading Collector</h3>
              <p>Publicly accessible meter reading collection form for field executives or residents to record current water readings.</p>
              <div className="action-link">
                <span>Open Data Entry</span> <FaArrowRight />
              </div>
            </div>

            {/* <div className="action-card" onClick={() => navigate("/executivereport")}>
              <div className="action-card-header">
                <div className="action-icon-box icon-summary">
                  <FaClipboardList />
                </div>
                <span className="action-tag tag-purple">Exec Summaries</span>
              </div>
              <h3>Executive Collection Reports</h3>
              <p>View consolidated payment and meter collection summaries with daily and monthly breakdowns.</p>
              <div className="action-link">
                <span>View Summaries</span> <FaArrowRight />
              </div>
            </div> */}
          </div>

          {/* Recent Readings Table Preview */}
          <div className="recent-section">
            <div className="recent-header">
              <div>
                <h2>Recent Meter Readings</h2>
                <p>Latest readings submitted across all society wings</p>
              </div>
              <button className="view-all-link" onClick={() => navigate("/adminreport")}>
                View All Readings ({stats.totalReadings}) <FaArrowRight />
              </button>
            </div>

            <div className="table-card">
              {stats.loading ? (
                <div className="table-loading">Loading recent readings...</div>
              ) : recentReadings.length === 0 ? (
                <div className="table-empty">No meter readings recorded yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Wing</th>
                        <th>Flat No</th>
                        <th>Prev Reading</th>
                        <th>Curr Reading</th>
                        <th>Consumption</th>
                        <th>Date</th>
                        <th>Image</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentReadings.map((row) => (
                        <tr key={row.id}>
                          <td><span className="wing-pill">{row.wing}</span></td>
                          <td><strong>{row.flatNo}</strong></td>
                          <td>{row.prevReading.toFixed(2)}</td>
                          <td><strong style={{ color: "#2563eb" }}>{row.currReading.toFixed(2)}</strong></td>
                          <td>
                            <span className="consumption-badge">
                              {(row.currReading - row.prevReading).toFixed(2)} units
                            </span>
                          </td>
                          <td>{row.date}</td>
                          <td>
                            <button className="table-img-btn" onClick={() => openImageModal(row.id)}>
                              <FaEye /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="workspace-footer">
          <p>© 2026 Life Republic 7 Avenue Co-op Housing Society | Powered by <strong>Dexpert Systems</strong></p>
        </footer>
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div className="image-modal-overlay" onClick={() => setModalImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Meter Photo Evidence</h3>
              <button className="modal-close" onClick={() => setModalImage(null)}>✕</button>
            </div>
            <div className="modal-body">
              <img src={modalImage} alt="Meter Proof" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
